import React, { useState, useRef } from 'react';
import { X, Palette, Eye, Upload, Trash2, Loader2, MessageSquare, Phone, Plus } from 'lucide-react';
import api from '../api';
import Toast from './Toast';
import { ChatImageSharingSetting } from './chatImageAttachments';

const POSITION_OPTIONS = [
  { name: 'Bottom Right', value: 'bottom-right' },
  { name: 'Bottom Left', value: 'bottom-left' },
  { name: 'Top Right', value: 'top-right' },
  { name: 'Top Left', value: 'top-left' },
];

const SIZE_OPTIONS = [
  { name: 'Small', value: 'small', px: 44 },
  { name: 'Medium', value: 'medium', px: 56 },
  { name: 'Large', value: 'large', px: 68 },
];

const ColorPicker = ({ label, fieldName, value, onChange, pickerId }) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-foreground">{label}</label>
    <div className="bg-muted/30 border border-border/60 rounded-xl p-3 flex items-center gap-3">
      <label htmlFor={pickerId} className="relative cursor-pointer flex-shrink-0">
        <div className="w-12 h-9 rounded-lg border-2 border-white shadow-md" style={{ backgroundColor: value }} />
        <input
          id={pickerId}
          type="color"
          value={value}
          onChange={(e) => onChange(fieldName, e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
      </label>
      <input
        type="text"
        value={value.replace('#', '')}
        onChange={(e) => {
          const v = '#' + e.target.value.replace('#', '');
          if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(fieldName, v);
        }}
        className="flex-1 px-3 py-2 text-sm font-mono bg-background border-2 border-border rounded-lg uppercase"
        maxLength={6}
      />
    </div>
  </div>
);

const Toggle = ({ checked, onChange, label }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
        checked ? 'bg-primary' : 'bg-muted'
      }`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`} />
    </button>
    <span className="text-sm text-muted-foreground">{label}</span>
  </div>
);

const ComboWidgetV2ThemeCustomizer = ({ agent, onClose, onSave }) => {
  const [settings, setSettings] = useState({
    combo_widget_v2_logo_url: agent?.combo_widget_v2_logo_url || null,
    combo_widget_v2_default_channel: agent?.combo_widget_v2_default_channel || 'chat',
    combo_widget_v2_button_label: agent?.combo_widget_v2_button_label || 'Contact us',
    combo_widget_v2_title: agent?.combo_widget_v2_title || agent?.name || 'Assistant',
    combo_widget_v2_subtitle: agent?.combo_widget_v2_subtitle || 'Talk or type — your call.',
    combo_widget_v2_tagline: agent?.combo_widget_v2_tagline || 'Real people. Real answers. Right now.',
    combo_widget_v2_teaser_message: agent?.combo_widget_v2_teaser_message || "Need help? We're online now.",
    combo_widget_v2_bg_color: agent?.combo_widget_v2_bg_color || '#020e1e',
    combo_widget_v2_surface_color: agent?.combo_widget_v2_surface_color || '#0a3161',
    combo_widget_v2_accent_color: agent?.combo_widget_v2_accent_color || '#769fc8',
    combo_widget_v2_launcher_color: agent?.combo_widget_v2_launcher_color || '#d11242',
    combo_widget_v2_launcher_size: agent?.combo_widget_v2_launcher_size || 'medium',
    combo_widget_v2_button_position: agent?.combo_widget_v2_button_position || 'bottom-right',
    combo_widget_v2_pulse_animation: agent?.combo_widget_v2_pulse_animation ?? true,
    combo_widget_v2_show_label: agent?.combo_widget_v2_show_label ?? true,
    combo_widget_v2_show_channel_switch: agent?.combo_widget_v2_show_channel_switch ?? true,
    combo_widget_v2_disclaimer_enabled: agent?.combo_widget_v2_disclaimer_enabled ?? false,
    chat_image_upload_enabled: agent?.chat_image_upload_enabled || false,
    combo_widget_v2_disclaimer_text: agent?.combo_widget_v2_disclaimer_text || '',
    combo_widget_v2_disclaimer_links: (Array.isArray(agent?.combo_widget_v2_disclaimer_links) && agent.combo_widget_v2_disclaimer_links.length > 0)
      ? agent.combo_widget_v2_disclaimer_links.map((l) => ({ text: l?.text || '', url: l?.url || '' }))
      : (agent?.combo_widget_v2_disclaimer_link_text || agent?.combo_widget_v2_disclaimer_link_url)
        ? [{ text: agent?.combo_widget_v2_disclaimer_link_text || '', url: agent?.combo_widget_v2_disclaimer_link_url || '' }]
        : [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  const set = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));
  const toggle = (field) => setSettings(prev => ({ ...prev, [field]: !prev[field] }));

  const addDisclaimerLink = () => setSettings(prev => ({
    ...prev,
    combo_widget_v2_disclaimer_links: [...prev.combo_widget_v2_disclaimer_links, { text: '', url: '' }],
  }));
  const updateDisclaimerLink = (index, field, value) => setSettings(prev => ({
    ...prev,
    combo_widget_v2_disclaimer_links: prev.combo_widget_v2_disclaimer_links.map((link, i) =>
      i === index ? { ...link, [field]: value } : link
    ),
  }));
  const removeDisclaimerLink = (index) => setSettings(prev => ({
    ...prev,
    combo_widget_v2_disclaimer_links: prev.combo_widget_v2_disclaimer_links.filter((_, i) => i !== index),
  }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        ...settings,
        combo_widget_v2_disclaimer_links: settings.combo_widget_v2_disclaimer_links.filter(
          (link) => link.text.trim() || link.url.trim()
        ),
      };
      await api.put(`/agents/${agent.id}`, payload);
      setToast({ message: 'Combo Widget V2 settings saved!', type: 'success' });
      setTimeout(() => {
        onSave?.(settings);
        onClose();
      }, 800);
    } catch {
      setToast({ message: 'Failed to save settings', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowed.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|webp|gif|svg)$/i)) {
      setUploadError('Please upload JPG, PNG, WebP, GIF, or SVG.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File too large. Maximum size is 5MB.');
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/agents/${agent.id}/combo-widget-v2/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set('combo_widget_v2_logo_url', response.data.file_url);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    try {
      await api.delete(`/agents/${agent.id}/combo-widget-v2/logo`);
      set('combo_widget_v2_logo_url', null);
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Failed to delete logo');
    }
  };

  const handlePreview = () => {
    const params = new URLSearchParams({
      view: 'button',
      bg: settings.combo_widget_v2_bg_color.replace('#', ''),
      surface: settings.combo_widget_v2_surface_color.replace('#', ''),
      accent: settings.combo_widget_v2_accent_color.replace('#', ''),
      launcher: settings.combo_widget_v2_launcher_color.replace('#', ''),
      launcherSize: settings.combo_widget_v2_launcher_size,
      position: settings.combo_widget_v2_button_position,
      channel: settings.combo_widget_v2_default_channel,
    });
    window.open(`${window.location.origin}/embed/combo-widget-v2/${agent.id}?${params}`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-elegant">
        <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <Palette className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Customize Combo Widget V2</h2>
              <p className="text-white/70 text-xs mt-0.5">Flagship dark navy design — logo, colors, and copy</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Live preview */}
          <div
            className="rounded-xl p-5 flex flex-col items-center gap-3"
            style={{ backgroundColor: settings.combo_widget_v2_bg_color }}
          >
            <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wide self-start">Preview</span>
            <div className="w-full flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                style={{ backgroundColor: settings.combo_widget_v2_surface_color }}
              >
                {settings.combo_widget_v2_logo_url ? (
                  <img src={settings.combo_widget_v2_logo_url} alt="Logo" style={{ width: '82%', height: '82%', objectFit: 'contain' }} />
                ) : (
                  <MessageSquare className="w-5 h-5" style={{ color: '#f4f5f5' }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate" style={{ color: '#f4f5f5' }}>
                  {settings.combo_widget_v2_title}
                </div>
                <div className="text-xs font-semibold" style={{ color: '#53b59e' }}>● Online now</div>
              </div>
            </div>
            <button
              type="button"
              className="w-full rounded-xl py-3 flex items-center justify-center gap-2 text-sm font-bold"
              style={{ backgroundColor: settings.combo_widget_v2_accent_color, color: '#020e1e' }}
            >
              <Phone className="w-4 h-4" />
              Start a voice call
            </button>
            <div
              className="rounded-full flex items-center justify-center self-end flex-shrink-0"
              style={{
                backgroundColor: settings.combo_widget_v2_launcher_color,
                width: SIZE_OPTIONS.find(s => s.value === settings.combo_widget_v2_launcher_size)?.px || 56,
                height: SIZE_OPTIONS.find(s => s.value === settings.combo_widget_v2_launcher_size)?.px || 56,
              }}
            >
              {settings.combo_widget_v2_logo_url ? (
                <img src={settings.combo_widget_v2_logo_url} alt="Logo" style={{ width: '82%', height: '82%', objectFit: 'contain' }} />
              ) : (
                <MessageSquare className="w-1/2 h-1/2 text-white" />
              )}
            </div>
          </div>

          {/* Logo upload */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-foreground">Brand Logo</label>
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,.jpg,.jpeg,.png,.webp,.gif,.svg"
                onChange={handleLogoUpload}
                className="hidden"
                id="combo-v2-logo-upload"
              />
              <label
                htmlFor="combo-v2-logo-upload"
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg flex items-center gap-2 text-sm cursor-pointer"
              >
                {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {isUploading ? 'Uploading…' : 'Upload Logo'}
              </label>
              {settings.combo_widget_v2_logo_url && (
                <button
                  type="button"
                  onClick={handleLogoDelete}
                  className="px-3 py-2 text-red-500 hover:bg-red-500/10 rounded-lg flex items-center gap-1.5 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              )}
            </div>
            {uploadError && <p className="text-xs text-red-500">{uploadError}</p>}
            <p className="text-xs text-muted-foreground">
              Shown on the launcher and inside the header avatar. JPG, PNG, WebP, GIF, or SVG up to 5MB. Falls back to a chat icon when no logo is set.
            </p>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-semibold text-foreground">Default Channel</label>
            <div className="grid grid-cols-2 gap-3">
              {['chat', 'voice'].map((ch) => (
                <button
                  key={ch}
                  type="button"
                  onClick={() => set('combo_widget_v2_default_channel', ch)}
                  className={`px-4 py-3 rounded-lg border-2 capitalize transition-all ${
                    settings.combo_widget_v2_default_channel === ch
                      ? 'border-primary bg-primary/10 text-primary font-semibold'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  {ch}
                </button>
              ))}
            </div>
          </div>

          <ChatImageSharingSetting
            enabled={settings.chat_image_upload_enabled}
            onChange={(value) => set('chat_image_upload_enabled', value)}
            llmModel={agent?.llm_model}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-foreground">Widget Title</label>
              <input
                type="text"
                value={settings.combo_widget_v2_title}
                onChange={(e) => set('combo_widget_v2_title', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-foreground">Accessible Launcher Label</label>
              <input
                type="text"
                value={settings.combo_widget_v2_button_label}
                onChange={(e) => set('combo_widget_v2_button_label', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground">Headline (first-open screen)</label>
            <input
              type="text"
              value={settings.combo_widget_v2_subtitle}
              onChange={(e) => set('combo_widget_v2_subtitle', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground">Tagline (below headline)</label>
            <input
              type="text"
              value={settings.combo_widget_v2_tagline}
              onChange={(e) => set('combo_widget_v2_tagline', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground">Teaser Bubble (shown near collapsed launcher)</label>
            <input
              type="text"
              value={settings.combo_widget_v2_teaser_message}
              onChange={(e) => set('combo_widget_v2_teaser_message', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ColorPicker
              label="Panel Background"
              fieldName="combo_widget_v2_bg_color"
              value={settings.combo_widget_v2_bg_color}
              onChange={set}
              pickerId="v2-bg"
            />
            <ColorPicker
              label="Surface / Bubble Color"
              fieldName="combo_widget_v2_surface_color"
              value={settings.combo_widget_v2_surface_color}
              onChange={set}
              pickerId="v2-surface"
            />
            <ColorPicker
              label="Accent Color (buttons, active tab)"
              fieldName="combo_widget_v2_accent_color"
              value={settings.combo_widget_v2_accent_color}
              onChange={set}
              pickerId="v2-accent"
            />
            <ColorPicker
              label="Launcher Color"
              fieldName="combo_widget_v2_launcher_color"
              value={settings.combo_widget_v2_launcher_color}
              onChange={set}
              pickerId="v2-launcher"
            />
          </div>

          <div className="space-y-3">
            <label className="text-sm font-semibold text-foreground">Launcher Size</label>
            <div className="grid grid-cols-3 gap-3">
              {SIZE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => set('combo_widget_v2_launcher_size', opt.value)}
                  className={`px-4 py-3 rounded-lg border-2 text-sm flex flex-col items-center gap-2 ${
                    settings.combo_widget_v2_launcher_size === opt.value
                      ? 'border-primary bg-primary/10 text-primary font-semibold'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <span
                    className="rounded-full"
                    style={{ width: opt.px * 0.4, height: opt.px * 0.4, backgroundColor: settings.combo_widget_v2_launcher_color }}
                  />
                  {opt.name}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-semibold text-foreground">Button Position</label>
            <div className="grid grid-cols-2 gap-3">
              {POSITION_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => set('combo_widget_v2_button_position', opt.value)}
                  className={`px-4 py-3 rounded-lg border-2 text-sm ${
                    settings.combo_widget_v2_button_position === opt.value
                      ? 'border-primary bg-primary/10 text-primary font-semibold'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  {opt.name}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4 bg-muted/20 rounded-xl p-4 border border-border">
            <Toggle
              checked={settings.combo_widget_v2_show_label}
              onChange={() => toggle('combo_widget_v2_show_label')}
              label="Show teaser bubble near the launcher"
            />
            <Toggle
              checked={settings.combo_widget_v2_pulse_animation}
              onChange={() => toggle('combo_widget_v2_pulse_animation')}
              label="Pulse animation on launcher button"
            />
            <Toggle
              checked={settings.combo_widget_v2_show_channel_switch}
              onChange={() => toggle('combo_widget_v2_show_channel_switch')}
              label="Show Message/Voice tab toggle once opened"
            />
          </div>

          <div className="space-y-3 bg-muted/20 rounded-xl p-4 border border-border">
            <Toggle
              checked={settings.combo_widget_v2_disclaimer_enabled}
              onChange={() => toggle('combo_widget_v2_disclaimer_enabled')}
              label="Show a disclaimer on the first-open screen (below the channel buttons)"
            />
            {settings.combo_widget_v2_disclaimer_enabled && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Disclaimer Text</label>
                  <textarea
                    value={settings.combo_widget_v2_disclaimer_text}
                    onChange={(e) => set('combo_widget_v2_disclaimer_text', e.target.value)}
                    rows={3}
                    placeholder="e.g. Calls and messages may be recorded for quality and training purposes."
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm resize-none"
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-foreground">Hyperlinked words (optional)</label>
                  {settings.combo_widget_v2_disclaimer_links.map((link, index) => {
                    const notFound = link.text.trim() && !settings.combo_widget_v2_disclaimer_text.includes(link.text);
                    return (
                      <div key={index} className="space-y-1">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                          <input
                            type="text"
                            value={link.text}
                            onChange={(e) => updateDisclaimerLink(index, 'text', e.target.value)}
                            placeholder="Exact word/phrase from the text above"
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="url"
                              value={link.url}
                              onChange={(e) => updateDisclaimerLink(index, 'url', e.target.value)}
                              placeholder="https://example.com/privacy"
                              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                            />
                            <button
                              type="button"
                              onClick={() => removeDisclaimerLink(index)}
                              className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg flex-shrink-0"
                              aria-label="Remove link"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        {notFound && (
                          <p className="text-xs text-amber-500">
                            Not found in the disclaimer text above — it will be appended after it instead of underlined inline.
                          </p>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={addDisclaimerLink}
                    className="px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-xs font-semibold flex items-center gap-1.5"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add link
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Enter the exact word or phrase from the disclaimer text that should become a clickable link, and the URL it should open. That phrase turns into an underlined link inline, in place, and opens in a new tab. Leave empty to show plain text only.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-border p-4 bg-muted/30 flex justify-between items-center flex-shrink-0">
          <button
            type="button"
            onClick={handlePreview}
            className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default ComboWidgetV2ThemeCustomizer;
